import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataTableDataSearchDto,
  InfoIconDto,
  VegaChartDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  portalFilenamify,
  triggerDownloadWithAnchorTag,
  WidgetProps,
} from '@asset-metrix/portal-ui';
import dayjs from 'dayjs';
import { View } from 'vega';

import { useColorScheme } from '../../app/usePortalTheme';
import { DOWNLOAD_DATE_PREFIX_FORMAT } from '../../config';
import { BASE_FONT_SIZE } from '../../utils/pixelSizeHelperPortalUi';
import {
  buildShareActionsWithInsertIndex,
  widgetGroupDataShareActions,
  widgetShareAction,
} from '../table/widgetShareAction';

import { ExtractedBinding } from './inputBinding/extraction';
import { getSelectedLabels, VegaState } from './inputBinding/vegaControlState';

const EXPORT_BACKGROUND_LIGHT_THEME = 'white';
const EXPORT_BACKGROUND_DARK_THEME = 'black';
const EXPORT_PADDING = BASE_FONT_SIZE;

export type BaseUseVegaChartActionProps = {
  activeCustomFilterIds: string[];
  customFilterConfigs: VegaChartDto['config']['customFilter'];
  infoIcon?: InfoIconDto;
  title: string;
  widgetId: string;
  closeFullscreen?: () => void;
  openCustomFilter: () => void;
  openInfoModal?: () => void;
  openFullscreen?: () => void;
};

export const useVegaChartActions = ({
  activeCustomFilterIds,
  bindings,
  config,
  controlState,
  customFilterConfigs,
  infoIcon,
  labelData,
  pageFilter,
  tableFilter,
  title,
  vegaViewRef,
  widgetId,
  closeFullscreen,
  openCustomFilter,
  openInfoModal,
  openFullscreen,
  stakeAdjusted,
}: BaseUseVegaChartActionProps & {
  bindings: ExtractedBinding[];
  config: VegaChartDto['config'];
  controlState: VegaState;
  labelData: object;
  pageFilter?: DataTableDataSearchDto['selection'];
  tableFilter?: DataTableDataSearchDto['selection'];
  vegaViewRef: RefObject<View | null>;
  stakeAdjusted?: boolean;
}) => {
  const { t } = useTranslation();
  const actions: WidgetProps['actions'] = [];

  if (customFilterConfigs.length > 0) {
    actions.push({
      icon:
        activeCustomFilterIds.length > 0
          ? { name: 'filter-active', svgIcon: true }
          : { name: 'filter', svgIcon: true },
      title: t('app.widget.action.label.customFilter'),
      cb: openCustomFilter,
    });
  }

  const isGroupData = config.vegaChartDataType === 'groupData';

  const shareActionKeys = new Set(
    config.shareActions?.map((sa) => sa.actionKey)
  );

  const canDownloadCsv =
    shareActionKeys.has('downloadCsv') ||
    shareActionKeys.has('downloadCsvUnfiltered');
  const canDownloadXlsx =
    shareActionKeys.has('downloadXlsx') ||
    shareActionKeys.has('downloadXlsxUnfiltered') ||
    shareActionKeys.has('downloadXlsxReadOnly');
  const readOnlyXlsx =
    !shareActionKeys.has('downloadXlsx') &&
    !shareActionKeys.has('downloadXlsxUnfiltered') &&
    shareActionKeys.has('downloadXlsxReadOnly');

  const {
    actions: configuredWidgetShareActions,
    insertIndex: groupDataInsertIndex,
  } = buildShareActionsWithInsertIndex(config.shareActions ?? [], (sa) =>
    widgetShareAction(widgetId, title, sa, t, {
      pageFilterSelections: pageFilter,
      tableFilterSelections: tableFilter,
      stakeAdjusted,
    })
  );

  const groupDataActions = isGroupData
    ? widgetGroupDataShareActions(widgetId, title, t, {
        canDownloadCsv,
        canDownloadXlsx,
        readOnlyXlsx,
        pageFilterSelections: pageFilter,
        tableFilterSelections: tableFilter,
        stakeAdjusted,
        groupByColumnId: config.groupedColumnForChart?.id,
      })
    : [];

  const [colorScheme] = useColorScheme();

  const getDownloadFilename = () => {
    const selectedLabels = getSelectedLabels(controlState, bindings, labelData);

    const baseName = title || `chart-export-${widgetId}`;
    const suffix =
      selectedLabels.length > 0 ? ` - ${selectedLabels.join(' - ')}` : '';

    return portalFilenamify(
      `${dayjs().format(DOWNLOAD_DATE_PREFIX_FORMAT)} ${baseName}${suffix}.png`
    );
  };

  const hardcodedWidgetShareActions = [
    {
      label: t('app.widget.action.label.imageDownload'),
      cb: async () => {
        const vegaView = vegaViewRef.current;
        if (vegaView != null) {
          triggerDownloadWithAnchorTag(
            await vegaView.toImageURL('png'),
            getDownloadFilename()
          );
        }
      },
    },
    {
      label: t('app.widget.action.label.imageDownloadWithBackground'),
      cb: async () => {
        const vegaView = vegaViewRef.current;
        if (vegaView != null) {
          const sourceCanvas = await vegaView.toCanvas();

          const canvas = document.createElement('canvas');
          canvas.width = sourceCanvas.width + EXPORT_PADDING;
          canvas.height = sourceCanvas.height + EXPORT_PADDING;

          const context = canvas.getContext('2d');
          if (context) {
            context.fillStyle =
              colorScheme === 'dark'
                ? EXPORT_BACKGROUND_DARK_THEME
                : EXPORT_BACKGROUND_LIGHT_THEME;
            context.fillRect(0, 0, canvas.width, canvas.height);

            context.drawImage(
              sourceCanvas,
              Math.round(EXPORT_PADDING / 2),
              Math.round(EXPORT_PADDING / 2)
            );
          }
          const dataUrl = canvas.toDataURL('png');

          triggerDownloadWithAnchorTag(dataUrl, getDownloadFilename());
        }
      },
    },
  ];

  const widgetShareActions = [
    ...hardcodedWidgetShareActions,
    ...configuredWidgetShareActions.slice(0, groupDataInsertIndex),
    ...groupDataActions,
    ...configuredWidgetShareActions.slice(groupDataInsertIndex),
  ];

  actions.push({
    icon: { name: 'share' },
    title: t('app.widget.action.label.shareActions'),
    moreActions: widgetShareActions,
  });

  if (infoIcon != null) {
    actions.push({
      icon: { name: 'info' },
      title: t('app.widget.action.label.additionalInformation'),
      cb: openInfoModal,
    });
  }

  if (openFullscreen) {
    actions.push({
      icon: { name: 'open_in_full' },
      title: t('app.widget.action.label.openInFullscreen'),
      cb: openFullscreen,
    });
  }
  if (closeFullscreen) {
    actions.push({
      icon: { name: 'close' },
      title: t('app.widget.action.label.closeFullscreen'),
      cb: closeFullscreen,
    });
  }

  return actions;
};
